import documentsMenuItemsProvider from "DocumentsMenuItemProvider";
import * as entityActionsChecker from "EntityActionsChecker";
import { getInterfaceName } from "EntityExtensions";
import { FormAction, type FormFlowUI, type FormFlowUIMenuOptions } from "FormFlowUITypes";
import type { MenuItemOptions } from "MenuItemsProvider";
import navigationService from "NavigationService";
import notesService from "NotesService";
import workflowMenuItemProvider from "WorkflowMenuItemProvider";
import ko from "knockout";
import { type WtgFrameworkActionMenuItem } from "wtg-material-ui";
import { runExclusiveAsync } from "./FormFlowUIExclusiveActions.ts";
import { refreshTaskAsync } from "./FormFlowUIMessageBus.ts";
import { runExclusiveWithSaveAsync } from "./FormFlowUISaveChanges.ts";

export async function buildFormFlowUIMenuAsync(formFlowUI: FormFlowUI): Promise<FormFlowUIMenuOptions> {
  const canShowMessages = await canShowMessagesAsync(formFlowUI);
  return {
    canShowDocuments: ko.pureComputed(() => canShowDocuments(formFlowUI)),
    canShowEDocs: ko.pureComputed(() => canShowEDocs(formFlowUI)),
    canShowLogs: ko.pureComputed(() => canShowLogs(formFlowUI)),
    canShowMessages,
    canShowNotes: ko.pureComputed(() => canShowNotes(formFlowUI)),
    canShowWorkflow: ko.pureComputed(() => canShowWorkflow(formFlowUI)),
    showEDocsAsync: showEDocsAsync.bind(undefined, formFlowUI),
    showMessagesAsync: showMessagesAsync.bind(undefined, formFlowUI),
    showNotesAsync: showNotesAsync.bind(undefined, formFlowUI),
    showLogsAsync: showLogsAsync.bind(undefined, formFlowUI),
    getDocumentMenuItemsAsync: getDocumentMenuItemsAsync.bind(undefined, formFlowUI),
  };
}

export function buildAdditionalMenuOptions(
  formFlowUI: FormFlowUI,
  menuOptions: FormFlowUIMenuOptions,
): MenuItemOptions {
  return {
    ...menuOptions,
    isTaskPage: true,
    workflowContext: formFlowUI.workflowContext,
    others: formFlowUI.others,
  };
}

function canShowDocuments(formFlowUI: FormFlowUI): boolean {
  const entity = formFlowUI.entity?.();
  return (
    !!entity &&
    actionEnabled(formFlowUI.disabledFormActions, FormAction.Documents) &&
    entityActionsChecker.canViewDocuments(getInterfaceName(entity.entityType))
  );
}

function canShowEDocs(formFlowUI: FormFlowUI): boolean {
  const entity = formFlowUI.entity?.();
  return (
    !!entity &&
    entityActionsChecker.canViewEDocs(getInterfaceName(entity.entityType)) &&
    actionEnabled(formFlowUI.disabledFormActions, FormAction.EDocs) &&
    !entityActionsChecker.hideEDocs(getInterfaceName(entity.entityType))
  );
}

async function canShowMessagesAsync(formFlowUI: FormFlowUI): Promise<boolean> {
  const entity = formFlowUI.entity?.();
  return (
    !!entity &&
    actionEnabled(formFlowUI.disabledFormActions, FormAction.Messages) &&
    (await entityActionsChecker.canShowMessagesAsync(entity.entityType))
  );
}

function canShowNotes(formFlowUI: FormFlowUI): boolean {
  const entity = formFlowUI.entity?.();
  return (
    !!entity &&
    actionEnabled(formFlowUI.disabledFormActions, FormAction.Notes) &&
    notesService.shouldShowNotes(entity.entityType)
  );
}

function canShowLogs(formFlowUI: FormFlowUI): boolean {
  const entity = formFlowUI.entity?.();
  return (
    !!entity &&
    actionEnabled(formFlowUI.disabledFormActions, FormAction.Logs) &&
    entityActionsChecker.canShowLogs(entity.entityType)
  );
}

function canShowWorkflow(formFlowUI: FormFlowUI): boolean {
  const entity = formFlowUI.entity?.();
  return (
    !!entity &&
    actionEnabled(formFlowUI.disabledFormActions, FormAction.Workflows) &&
    entityActionsChecker.canShowWorkflow(entity.entityType)
  );
}

function actionEnabled(disabledFormActions: FormAction[] | undefined, action: FormAction): boolean {
  return !disabledFormActions?.includes(action);
}

async function showEDocsAsync(formFlowUI: FormFlowUI): Promise<void> {
  const entity = formFlowUI.entity?.();
  if (!entity) {
    return;
  }
  if (entity.entityAspect.entityState.isAdded()) {
    return await runExclusiveWithSaveAsync(formFlowUI, () => {
      return navigationService.post("#/edocs", { entity });
    });
  } else {
    return await runExclusiveAsync(formFlowUI, () => {
      return navigationService.post("#/edocs", { entity });
    });
  }
}

async function showMessagesAsync(formFlowUI: FormFlowUI): Promise<void> {
  const result = await runExclusiveAsync(formFlowUI, async () => {
    const entity = formFlowUI.entity?.();
    if (entity) {
      return await navigationService.post("#/conversation/showMessages", { entity });
    }
  });
  return result;
}

async function showNotesAsync(formFlowUI: FormFlowUI): Promise<void> {
  return await runExclusiveWithSaveAsync(formFlowUI, () => {
    const entity = formFlowUI.entity?.();
    return (
      entity &&
      notesService.showNotesAsync(entity.entityAspect.getKey(), async (hasSaved: boolean) => {
        if (hasSaved) {
          await refreshTaskAsync(formFlowUI);
        }
      })
    );
  });
}

async function showLogsAsync(formFlowUI: FormFlowUI): Promise<void> {
  await runExclusiveWithSaveAsync(formFlowUI, async () => {
    const entity = formFlowUI.entity?.();
    return await workflowMenuItemProvider.showWorkflowAuditLogsAsync(entity);
  });
}

async function getDocumentMenuItemsAsync(formFlowUI: FormFlowUI): Promise<WtgFrameworkActionMenuItem[]> {
  const entity = formFlowUI.entity?.();
  if (!entity) {
    return [];
  }
  return await documentsMenuItemsProvider.getDocumentMenuItemsAsync(entity);
}
